import { useEffect, useState } from 'react';
import api, { fmtRp, errMsg, branchLabel, todayStr } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Printer, Ban, Loader2, Eye } from 'lucide-react';
import Receipt from '../components/Receipt';

export default function Riwayat() {
  const { user, branches } = useAuth();
  const isAdmin = user.role === 'admin';
  const [txs, setTxs] = useState(null);
  const [branch, setBranch] = useState('');
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());
  const [detail, setDetail] = useState(null);
  const [cancelTx, setCancelTx] = useState(null);
  const [reason, setReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const load = () => {
    api.get('/transactions', { params: { branch, from_date: from, to_date: to } })
      .then((r) => setTxs(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [branch, from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  const doCancel = async () => {
    setCancelling(true);
    try {
      await api.post(`/transactions/${cancelTx.id}/cancel`, { reason });
      toast.success(`Transaksi ${cancelTx.invoice_no} dibatalkan, stok dikembalikan`);
      setCancelTx(null);
      setReason('');
      setDetail(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="space-y-5 animate-fade-up" data-testid="riwayat-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Riwayat Transaksi</h2>
          <p className="text-sm text-muted-foreground">Lihat struk ulang{isAdmin ? ' & batalkan transaksi' : ''}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && (
            <select value={branch} onChange={(e) => setBranch(e.target.value)} className="lodi-input w-auto" data-testid="riwayat-branch-select">
              <option value="">Semua Cabang</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="lodi-input w-auto" data-testid="riwayat-from-input" />
          <span className="text-muted-foreground text-sm">—</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="lodi-input w-auto" data-testid="riwayat-to-input" />
        </div>
      </div>

      <div className="lodi-card overflow-hidden" data-testid="riwayat-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground uppercase tracking-wide border-b border-line bg-elevated/50">
                <th className="px-4 py-3">Invoice</th>
                <th className="px-4 py-3">Waktu</th>
                <th className="px-4 py-3">Kasir</th>
                <th className="px-4 py-3">Cabang</th>
                <th className="px-4 py-3">Pembeli</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {!txs ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center"><Loader2 className="w-6 h-6 text-brand animate-spin mx-auto" /></td></tr>
              ) : txs.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground" data-testid="riwayat-empty">Belum ada transaksi pada periode ini</td></tr>
              ) : (
                txs.map((t) => (
                  <tr key={t.id} className="border-b border-line/50 last:border-0 hover:bg-elevated/40" data-testid={`riwayat-row-${t.id}`}>
                    <td className="px-4 py-2.5 font-mono text-xs">{t.invoice_no}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{t.date_wib} {t.time_wib}</td>
                    <td className="px-4 py-2.5">{t.cashier_name}</td>
                    <td className="px-4 py-2.5">{branchLabel(t.branch)}</td>
                    <td className="px-4 py-2.5">{t.customer_name || '-'}</td>
                    <td className="px-4 py-2.5 text-center">
                      {t.status === 'completed'
                        ? <span className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/25 px-2 py-0.5 rounded-full text-xs font-semibold">Selesai</span>
                        : <span className="bg-brand/10 text-brand border border-brand/25 px-2 py-0.5 rounded-full text-xs font-semibold">Dibatalkan</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold text-emerald-400">{fmtRp(t.total)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex justify-end gap-1.5">
                        <button onClick={() => setDetail(t)} data-testid={`view-tx-${t.id}`} title="Lihat struk"
                          className="w-8 h-8 rounded-lg bg-elevated border border-line flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors">
                          <Eye className="w-4 h-4" />
                        </button>
                        {isAdmin && t.status === 'completed' && (
                          <button onClick={() => setCancelTx(t)} data-testid={`cancel-tx-${t.id}`} title="Batalkan"
                            className="w-8 h-8 rounded-lg bg-brand/10 border border-brand/25 text-brand flex items-center justify-center hover:bg-brand/20 transition-colors">
                            <Ban className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="bg-surface border-line max-w-sm" data-testid="tx-detail-dialog">
          <DialogHeader><DialogTitle className="font-display">Detail Struk</DialogTitle></DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto py-2">
            <Receipt tx={detail} />
          </div>
          {detail?.status === 'cancelled' && (
            <p className="text-xs text-muted-foreground text-center">Dibatalkan oleh {detail.cancelled_by}{detail.cancel_reason ? ` — "${detail.cancel_reason}"` : ''}</p>
          )}
          <button onClick={() => window.print()} data-testid="reprint-receipt-button"
            className="w-full py-3 rounded-xl bg-brand hover:bg-branddark text-white font-display font-bold flex items-center justify-center gap-2 transition-all active:scale-[0.98]">
            <Printer className="w-4 h-4" /> Cetak Ulang
          </button>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!cancelTx} onOpenChange={(v) => !v && setCancelTx(null)}>
        <AlertDialogContent className="bg-surface border-line" data-testid="cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Batalkan Transaksi?</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelTx?.invoice_no} senilai {fmtRp(cancelTx?.total)} akan dibatalkan dan stok dikembalikan ke {branchLabel(cancelTx?.branch)}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Alasan pembatalan (opsional)" className="lodi-input" data-testid="cancel-reason-input" />
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-elevated border-line" data-testid="cancel-abort-button">Kembali</AlertDialogCancel>
            <AlertDialogAction onClick={doCancel} disabled={cancelling} className="bg-brand hover:bg-branddark text-white" data-testid="cancel-confirm-button">
              {cancelling ? 'Memproses...' : 'Ya, Batalkan'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
